import { useCallback, useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"
import { useNavigate } from "react-router-dom"
import _ from "lodash"

//Assessments components:
import { assessmentsApiCompleteListPost } from "../assessmentsApi"
import ParticipantAssessmentFilters from "./ParticipantAssessmentsFilters"

// Context
import { UserContext } from "../../../context/userContext"

//Helpers
import { LoaderMedium } from "../../../components/Loaders"
import { LogError } from "../../../helpers/logger"
import ROUTES from "../../../components/Routes/routesContants"

//PrimeReact
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { Paginator } from "primereact/paginator"
import { Dropdown } from "primereact/dropdown"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"
import ParticipantAssessmentFacilitator from "./ParticipantAssessmentFacilitator"
import { navigateWithOrigin } from "../../../helpers/navigation"
import { formatCompletedDateToLocaleString } from "../assessmentHelpers"

export default function ParticipantAssessmentsCompleted({
  facilitatorProfiles,
  triggerRefresh,
  onView,
  onSaveFacilitatorProfile,
}) {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [dataset, setDataset] = useState(undefined)
  const [filters, setFilters] = useState({
    sortField: "completedDate",
    sortOrder: "z-a",
    rows: 5,
    first: 0,
    page: 0,
  })
  const [additionalFilters, setAdditionalFilters] = useState({})
  const [showFilterDialog, setShowFilterDialog] = useState(false)
  const [facilitators, setFacilitators] = useState(facilitatorProfiles)

  const loadAssessments = useCallback(async () => {
    try {
      setIsLoading(true)

      let useAdditionalFilters = _.cloneDeep(additionalFilters)

      //Check for date filters and convert to ISO DATE only string if they exist
      const dateKeys = ["completedTo", "completedFrom"]
      dateKeys.forEach((key) => {
        if (useAdditionalFilters[key]) {
          useAdditionalFilters[key] = DateTime.fromJSDate(
            useAdditionalFilters[key],
          ).toISODate()
        }
      })

      let payload = {
        ...filters,
        ...useAdditionalFilters,
        organizationId: userCtx.session.user.organization_id,
        participantId: userCtx.participant_id,
        pageSize: filters.rows,
        pageNum: filters.page + 1,
      }

      const result = await assessmentsApiCompleteListPost(
        userCtx.tokenAssessments,
        payload,
      )

      setDataset(result)
      setIsLoading(false)
    } catch (error) {
      const message = "Unable to load completed assessments"

      LogError(message, error)
      userCtx.setNotificationError(message)

      setDataset(undefined)
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    additionalFilters,
    userCtx.participant_id,
    userCtx.session.user.organization_id,
    triggerRefresh,
  ])

  useEffect(() => {
    loadAssessments()
  }, [loadAssessments])

  const viewSession = (templateId, sessionId) => {
    const route = ROUTES.FORM_VIEW_MODE.replace(
      ":idTemplate",
      templateId,
    ).replace(":idStamp", sessionId)
    navigateWithOrigin(navigate, route, { replace: true })
  }

  const RenderActionBody = (rowData) => {
    let iconName = "visibility"
    let action = onView
    let tooltip = "View assessment form"

    return (
      <div className={assessStyles.actionButtonContainer}>
        <Button
          type="button"
          onClick={() => {
            action(rowData.id)
          }}
          className={assessStyles.actionButtonPurple}
          tooltip={tooltip}
          tooltipOptions={{ position: "bottom" }}
        >
          <span className={`material-icons`}>{iconName}</span>
        </Button>
        <Button
          type="button"
          onClick={() => {
            viewSession(rowData.templateId, rowData.sessionId)
          }}
          className={assessStyles.actionButtonPurple}
          tooltip={
            !rowData?.templateId
              ? "No template recorded to display"
              : "View session"
          }
          tooltipOptions={{ position: "bottom" }}
          disabled={!rowData?.templateId || !rowData?.sessionId}
        >
          <span className={`material-icons`}>{"view_compact_alt"}</span>
        </Button>
      </div>
    )
  }

  const CompletedDateTemplate = (rowData) => {
    let date = formatCompletedDateToLocaleString(rowData.completedDate)
    return <>{date}</>
  }

  return (
    <>
      {showFilterDialog ? (
        <ParticipantAssessmentFilters
          filters={additionalFilters}
          onSetFilters={(newFilters) => {
            setAdditionalFilters(newFilters)
            setShowFilterDialog(false)
          }}
          onClose={() => {
            setShowFilterDialog(false)
          }}
        />
      ) : null}
      <div className={myStyles.completedLayout}>
        <div className={myStyles.completedLayoutHeader}></div>
        <div className={myStyles.completedLayoutFilters}>
          <Button
            className={`${myStyles.actionButton}`}
            onClick={() => {
              setShowFilterDialog(true)
            }}
          >
            <span className={`${myStyles.filterButtonText}`}>{`Filters${
              Object.keys(additionalFilters).length > 0
                ? ` [${Object.keys(additionalFilters).length}]`
                : ""
            }`}</span>
            <span className={`material-icons`}>{"tune"}</span>
          </Button>
        </div>
        <div className={myStyles.completedLayoutSort}>
          <Dropdown
            className={myStyles.completedComponent}
            value={filters.sortField}
            options={[
              { label: "Sort by: Name", value: "assessmentName" },
              { label: "Sort by: Description", value: "assessmentDescription" },
              { label: "Sort by: Score", value: "score" },
              { label: "Sort by: Completed date", value: "completedDate" },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => {
              setFilters({ ...filters, sortField: e.value })
            }}
          />
          <div className="buttonContainer">
            <Button
              className={`${assessStyles.button} ${myStyles.refreshButton}`}
              onClick={() => {
                loadAssessments()
              }}
              tooltip={`${"Refresh data"}`}
              tooltipOptions={{ position: "bottom" }}
            >
              <span className={`material-icons`}>refresh</span>
            </Button>
          </div>
        </div>
        <div className={myStyles.completedLayoutBody}>
          {isLoading ? (
            <LoaderMedium />
          ) : (
            <>
              <DataTable
                value={dataset?.assessments || []}
                sortField={filters.sortField}
                sortOrder={filters.sortOrder === "a-z" ? 1 : -1}
                onSort={(sort) => {
                  const sortOrder = sort.sortOrder === 1 ? "a-z" : "z-a"
                  setFilters({
                    ...filters,
                    sortField: sort.sortField,
                    sortOrder: sortOrder,
                  })
                }}
                className={assessStyles.dataTable}
              >
                <Column
                  header={"Name"}
                  field={"assessmentName"}
                  sortable
                ></Column>
                <Column
                  header={"Description"}
                  field={"assessmentDescription"}
                  sortable
                ></Column>
                <Column header={"Score"} field={"score"} sortable></Column>
                <Column
                  header={"Completed date"}
                  body={CompletedDateTemplate}
                  field={"completedDate"}
                  sortable
                ></Column>
                <Column
                  header={"Facilitator"}
                  body={(rowData) => {
                    return (
                      <ParticipantAssessmentFacilitator
                        assessment={{
                          ...rowData,
                          organization_id: rowData?.organizationId,
                        }}
                        facilitatorProfiles={facilitators}
                        onSaveFacilitatorProfile={(
                          profile_id,
                          organization_id,
                          name,
                        ) => {
                          const newFacilitators = onSaveFacilitatorProfile(
                            profile_id,
                            organization_id,
                            name,
                          )

                          if (
                            newFacilitators &&
                            newFacilitators !== facilitators
                          ) {
                            setFacilitators(newFacilitators)
                          }
                        }}
                      />
                    )
                  }}
                />
                <Column body={RenderActionBody} />
              </DataTable>
              <Paginator
                first={dataset ? (dataset?.pageNum - 1) * dataset?.pageSize : 0}
                rows={dataset?.pageSize || 0}
                totalRecords={dataset?.numTotal || 0}
                onPageChange={(setup) => {
                  setFilters({ ...filters, ...setup })
                }}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}
